import React, { useEffect, useState } from 'react';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';

import { useLoader } from '../../loader/LoaderContext';
import stripeApiService from '../../services/stripe-subscription.service';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, Link } from 'react-router';
import PlanFeaturesPopoverInfo from '../../components/PopoverInfoIcon/PlanFeaturesPopoverInfo';
import { toast } from 'react-toastify';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useStripe } from '../../contexts/StripeContext';

function Subscription() {
  const subscription = useSubscription();
  const { stripePromise } = useStripe();
  const [productList, setProductList] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual'); // Default to annual
  const [expandedPlans, setExpandedPlans] = useState({}); // Track expanded state by product ID
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const location = useLocation();
  const userAttributes = useSelector((state) => state.auth.userAttributes);
  const organizationAttributes = useSelector(
    (state) => state.auth.organization,
  );

  // Check if this is a plan change or new subscription
  const isChangingPlan = location.state?.changePlan || false;
  const currentPlanId = userAttributes?.['custom:selected_price_id'];
  // Detect free trial user (active or expired) — should skip trial badge and go straight to paid activation.
  const isTrialExpiredActivation = organizationAttributes?.status === 'free_trial';

  useEffect(() => {
    const createCheckoutSession = async () => {
      try {
        showLoader();
        const quantity = 1;
        
        // Get promo code from URL query params if present
        const urlParams = new URLSearchParams(window.location.search);
        const urlPromoCode = urlParams.get('promo');
        
        // Call API to create a Checkout Session
        const checkoutSessionResponse =
          await stripeApiService.createCheckoutSession(
            selectedPlan.id,
            userAttributes.email,
            quantity,
            isTrialExpiredActivation,
            urlPromoCode,
          );

        // Load Stripe and redirect to checkout
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: checkoutSessionResponse.sessionId,
        });

        if (error) {
          toast.error(
            'We encountered an issue while starting the payment process. Please try again or contact support if the problem persists.',
          );
        }
      } catch (err) {
        toast.error(
          'An error occurred while initiating the checkout session. Please refresh the page and try again.',
        );
      } finally {
        hideLoader();
      }
    };

    if (selectedPlan) {
      createCheckoutSession();
    }
  }, [selectedPlan]);

  useEffect(() => {
    const fetchProductListData = async () => {
      try {
        showLoader();
        const productListResponse = await stripeApiService.getProductList();

        setProductList(productListResponse.products);
      } catch (err) {
        console.error('Error while fetching the product list');
      } finally {
        hideLoader();
      }
    };

    // Show plan selection for: first-time users (isActive===null) OR expired free trial
    if (!Object.is(subscription.isActive, null) && !isTrialExpiredActivation) {
      navigate('/landing', { replace: true });
      return;
    }

    // Auto-select plan if user previously picked one (non-trial path)
    if (Object.is(subscription.isActive, null) && userAttributes['custom:selected_price_id']) {
      setSelectedPlan({ id: userAttributes['custom:selected_price_id'] });
      return;
    }

    fetchProductListData();
  }, []);

  const handleChoosePlan = (plan, planType) => {
    const productSelected = productList.find(
      (element) => element.product.id === plan.id,
    );

    const priceObject = productSelected.prices.find(
      (p) => p.recurring.interval === planType,
    );
    setSelectedPlan(priceObject);
  };

  const formatPrice = (amount) => {
    return (amount / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const toggleFeatures = (productId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const shouldExcludeFeature = (featureName) => {
    const lowerName = featureName.toLowerCase();
    return (
      lowerName.includes('trial') ||
      lowerName.includes('onboarding') ||
      lowerName.includes('free trial') ||
      lowerName.includes('start-up') ||
      lowerName.includes('startup') ||
      lowerName.includes('start up')
    );
  };

  // Highlight text mapping for each plan
  const highlightTexts = {
    'Practice': 'Save 60+ admin hours each month for less than $2/day.',
    'Clinic': 'Give your staff the shared visibility they need to resolve coverage questions faster, reduce callbacks, and keep patient flow moving.',
    'Group': 'Ensure your team gets faster answers, more support, and a consistent, high-level experience across the organization.'
  };

  // Subtitle text mapping for each plan (under seat count)
  const seatSubtitles = {
    'Practice': 'Enough for a provider, an MA, and admin or support staff',
    'Clinic': 'Ideal for multiple providers and support staff.',
    'Group': 'Built for large or multi-site teams across roles.'
  };

  // Plan description mapping (below free trial)
  const planDescriptions = {
    'Practice': 'Perfect for solo practitioners or small practices who want to eliminate coverage confusion and stop losing time to pharmacy callbacks.',
    'Clinic': 'Built for busy clinics where multiple team members handle coverage, prior auth workflows, and patient communication. Keep your entire team aligned with real-time insurance clarity.',
    'Group': 'Designed for multi-site practices and high-volume clinics that need guaranteed speed, coordinated teamwork, and a partnership-level experience.'
  };

  // Helper method to check if a plan is active
  const isActivePlan = (prices, planType) => {
    if (!currentPlanId) return false;

    // Find the price object matching the plan type (monthly/yearly)
    const priceObject = prices.find(
      (price) => price.recurring.interval === planType,
    );

    // Compare the found price ID with current plan ID
    return priceObject?.id === currentPlanId;
  };

  // Helper to get price based on interval
  const getPriceByInterval = (prices, interval) => {
    return prices.find((price) => price.recurring.interval === interval);
  };

  if (!productList) return null;

  return (
    <div className="innerpage p-0">
      <div className={'container plan-container subscription-top'}>
        {productList && (
          <>
            <h1 className="text-center choose-plan">
              {isChangingPlan
                ? 'Change Your Plan'
                : isTrialExpiredActivation
                ? 'Activate Your Subscription'
                : 'Simple Pricing for Private Practices'}
            </h1>
            <p className="text-center">
              {isChangingPlan
                ? 'Choose the plan that works for your practice'
                : isTrialExpiredActivation
                ? 'Choose a plan to continue accessing all features'
                : 'Start your 14 day free trial today!'}
            </p>
            
            {/* Modern Billing Toggle */}
            <div className="billing-toggle-container">
              <div className="billing-toggle">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                  type="button"
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
                  type="button"
                >
                  <span>Annual</span>
                  <span className="save-badge">Save 20%</span>
                </button>
              </div>
            </div>

            {/* Unified Pricing Cards */}
            <div className="row my-60 justify-content-center">
              {productList
                .slice()
                .reverse()
                .map(({ product, prices }) => {
                  const interval = billingCycle === 'annual' ? 'year' : 'month';
                  const price = getPriceByInterval(prices, interval);
                  const isCurrentPlan = isActivePlan(prices, interval);
                  const isPopular = product?.metadata?.is_popular === '1';
                  const isExpanded = expandedPlans[product.id] || false;
                  
                  // Extract metadata
                  const seatCount = product?.metadata?.No_of_users || product?.metadata?.No_of_providers || '2';
                  const planDescription = product?.description || '';
                  
                  return (
                    <div
                      key={product.id}
                      className="col-lg-4 col-md-6 col-12"
                    >
                      <div
                        className={`subscription-box ${isPopular || isCurrentPlan ? 'active-subscription' : ''}`}
                      >
                        {isPopular && !isChangingPlan && (
                          <div className="most-popular-badge">Most Popular</div>
                        )}
                        {isCurrentPlan && isChangingPlan && (
                          <div className="current-plan">Current Plan</div>
                        )}
                        <div className="sb-card-header">
                          <h3>{product.name}</h3>
                        </div>
                        <h1>
                          ${formatPrice(price.unit_amount)}{' '}
                          <span>/{interval}</span>
                        </h1>
                        <p className="seat-count"><strong>Includes {seatCount} seats</strong></p>
                        <p className="seat-subtitle">{seatSubtitles[product.name] || ''}</p>
                        {!isChangingPlan && !isTrialExpiredActivation && (
                          <p className="trial-text">
                            14-Day Free Trial
                          </p>
                        )}
                        <p className="plan-description">{planDescriptions[product.name] || planDescription}</p>
                        
                        {/* Collapsible Learn More Section */}
                        <div className="collapsible-section">
                          <button
                            onClick={() => toggleFeatures(product.id)}
                            className="btn btn-outline-secondary learn-more-toggle"
                            type="button"
                          >
                            <span>{isExpanded ? 'Hide Details' : 'Learn More'}</span>
                            <IconChevronDown 
                              size={16} 
                              className={`chevron ${isExpanded ? 'expanded' : ''}`}
                            />
                          </button>
                          
                          {/* Collapsible Feature List */}
                          {isExpanded && (
                            <div className="feature-list-expanded">
                              <ul className="plan-list">
                                {product.marketing_features
                                  .filter(feature => !shouldExcludeFeature(feature.name))
                                  .map((feature, index) => (
                                    <li key={index}>
                                      <IconCheck size={16} />
                                      {feature.name}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        {/* Highlight Text */}
                        <p className="plan-highlight-text">
                          {highlightTexts[product.name] || ''}
                        </p>
                        
                        {product?.metadata?.footer_text && (
                          <p className="plan-footer-text">
                            {product.metadata.footer_text}
                          </p>
                        )}
                        <div>
                          <button
                            onClick={() => handleChoosePlan(product, interval)}
                            className={`btn ${isPopular ? 'btn-primary' : 'btn-outline-primary'}`}
                            disabled={isCurrentPlan}
                          >
                            {isChangingPlan
                              ? isCurrentPlan
                                ? 'Current Plan'
                                : 'Switch Plan'
                              : isTrialExpiredActivation
                              ? 'Activate Subscription'
                              : 'Start Free Trial'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            
            {/* Contact Support Message */}
            <div className="text-center mt-5 mb-4">
              <p className="text-muted mb-0">
                Need more than 20 seats?{' '}
                <Link 
                  to="/support/feedback" 
                  className="text-primary text-decoration-none fw-medium"
                >
                  Contact Support
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Subscription;
